import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlaneLanding, RefreshCw } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const STATUS_TONE = {
  Delayed: "border-amber-400/50 bg-amber-400/10 text-amber-300",
  Canceled: "border-red-500/50 bg-red-500/10 text-red-300",
  CanceledUncertain: "border-red-500/50 bg-red-500/10 text-red-300",
  Arrived: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  Expected: "border-ink-600 bg-ink-900 text-slate-300",
  EnRoute: "border-sky-500/40 bg-sky-500/10 text-sky-300",
};

const clock = (utc) => {
  if (!utc) return "--:--";
  const d = new Date(utc.replace(" ", "T").replace("Z", "+00:00"));
  return Number.isNaN(d.getTime())
    ? "--:--"
    : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export const ArrivalsBoard = () => {
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ note: "", tracking_enabled: true });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (refresh = false) => {
    if (refresh) setBusy(true);
    try {
      const { data } = await api.get("/dispatch/arrivals", { params: refresh ? { refresh: true } : {} });
      setRows(data.arrivals);
      setMeta({ note: data.note, tracking_enabled: data.tracking_enabled });
      setError("");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(() => load(), 120000);
    return () => clearInterval(timer);
  }, [load]);

  return (
    <div data-testid="arrivals-board" className="rounded-xl border border-ink-600 bg-ink-800/60 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
          <PlaneLanding className="h-3.5 w-3.5" /> Arrivals{rows ? ` · ${rows.length}` : ""}
        </p>
        <button
          type="button"
          data-testid="arrivals-refresh"
          onClick={() => load(true)}
          disabled={busy}
          className="flex items-center gap-2 rounded-full border border-ink-600 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-300 transition-colors hover:border-gold/50 hover:text-gold disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Checking flights" : "Check flights now"}
        </button>
      </div>

      {error && <p data-testid="arrivals-error" className="mt-3 text-sm text-red-400">{error}</p>}
      {meta.note && <p data-testid="arrivals-note" className="mt-3 text-xs text-amber-300">{meta.note}</p>}

      {!rows && <div className="mt-4 h-24 animate-pulse rounded-lg border border-ink-600 bg-ink-800" />}

      {rows?.length === 0 && (
        <p data-testid="arrivals-empty" className="mt-4 text-sm text-slate-500">
          No airport runs on the board for today or tomorrow. Add a flight number to a trip and its
          arrival shows up here.
        </p>
      )}

      <div className="mt-4 space-y-2.5">
        {rows?.map((a) => {
          const f = a.flight;
          const delayed = (f?.delay_minutes || 0) > 0;
          return (
            <div
              key={a.id}
              data-testid={`arrival-${a.number}`}
              className="grid grid-cols-1 gap-2 rounded-lg border border-ink-600 bg-ink-900/70 p-3.5 sm:grid-cols-[auto_1fr_auto] sm:items-center sm:gap-4"
            >
              <div className="font-mono text-xs">
                <p className="text-gold">{[a.airline, a.flight_number].filter(Boolean).join(" ")}</p>
                <p className="mt-1 text-slate-500">
                  {a.trip_date} · pickup{" "}
                  <span className={a.original_pickup_time ? "text-amber-300" : "text-slate-300"}>
                    {a.pickup_time}
                  </span>
                  {a.original_pickup_time && (
                    <span className="text-slate-600 line-through"> {a.original_pickup_time}</span>
                  )}
                </p>
              </div>

              <div className="text-sm">
                <p className="text-slate-100">
                  <Link to={`/app/manifests/${a.id}`} className="font-mono text-xs text-gold hover:underline">
                    #{a.number}
                  </Link>{" "}
                  {a.customer_name}
                </p>
                <p className="mt-1 text-[11px] text-slate-400">
                  {a.chauffeur_name || "Unassigned"}
                  {f?.arrival_airport ? ` · lands ${f.arrival_airport}` : ""}
                  {f?.arrival_terminal || a.terminal ? ` · terminal ${f?.arrival_terminal || a.terminal}` : ""}
                </p>
              </div>

              <div className="sm:text-right">
                {f ? (
                  <>
                    <span
                      data-testid={`arrival-status-${a.number}`}
                      className={`inline-block rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${
                        STATUS_TONE[f.status] || "border-ink-600 bg-ink-900 text-slate-300"
                      }`}
                    >
                      {delayed ? `${Math.round(f.delay_minutes)} min late` : f.status || "Scheduled"}
                    </span>
                    <p className="mt-1.5 font-mono text-[11px] text-slate-400">
                      {clock(f.estimated_arrival_utc || f.scheduled_arrival_utc)} local arrival
                    </p>
                  </>
                ) : (
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    {meta.tracking_enabled ? "not checked yet" : "tracking off"}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
